// src/pages/Automations/components/ActivityTab.jsx
// Activity history tab showing sent automations (Light theme)

import React from 'react';
import {
  Mail,
  MessageSquare,
  Check,
  X,
  Clock,
  Eye,
  RefreshCw,
  Download,
  TrendingUp,
  Send,
  AlertCircle
} from 'lucide-react';

export function ActivityTab({ activity, analytics, onResend, loading }) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'delivered':
        return { icon: Check, color: 'text-green-700', bg: 'bg-green-100', label: 'Delivered' };
      case 'opened':
        return { icon: Eye, color: 'text-blue-700', bg: 'bg-blue-100', label: 'Opened' };
      case 'failed':
        return { icon: X, color: 'text-red-700', bg: 'bg-red-100', label: 'Failed' };
      case 'bounced':
        return { icon: AlertCircle, color: 'text-orange-700', bg: 'bg-orange-100', label: 'Bounced' };
      case 'pending':
        return { icon: Clock, color: 'text-yellow-700', bg: 'bg-yellow-100', label: 'Pending' };
      default:
        return { icon: Send, color: 'text-gray-700', bg: 'bg-gray-100', label: status || 'Sent' };
    }
  };

  const handleExport = () => {
    window.open(`${process.env.REACT_APP_API_URL || ''}/api/automations/activity/export`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Send className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-gray-500">Total Sent</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{analytics.totalSent || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-500">Delivered</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{analytics.delivered || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-gray-500">Opened</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{analytics.opened || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-500">Open Rate</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{analytics.openRate || 0}%</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <X className="w-4 h-4 text-red-600" />
              <span className="text-xs text-gray-500">Failed</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{analytics.failed || 0}</p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-orange-600" />
              <span className="text-xs text-gray-500">Bounced</span>
            </div>
            <p className="text-2xl font-bold text-gray-800">{analytics.bounced || 0}</p>
          </div>
        </div>
      )}

      {/* Activity List Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Recent Activity</h3>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Activity List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500">Loading activity...</p>
          </div>
        ) : activity.length === 0 ? (
          <div className="p-8 text-center">
            <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No automation activity yet</p>
            <p className="text-sm text-gray-400 mt-1">Sent messages will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {activity.map((item) => {
              const status = getStatusInfo(item.status);
              const StatusIcon = status.icon;

              return (
                <div
                  key={item.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Type Icon */}
                    <div className={`p-2 rounded-lg ${item.type === 'email' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                      {item.type === 'email' ? (
                        <Mail className="w-4 h-4 text-purple-600" />
                      ) : (
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-800 truncate">
                          {item.automation_name || 'Automation'}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-500 truncate">
                          {item.client_name || item.recipient}
                        </span>
                      </div>

                      {item.subject && (
                        <p className="text-sm text-gray-500 truncate mb-1">{item.subject}</p>
                      )}

                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-400">{formatDate(item.sent_at)}</span>
                        {item.delivered_at && item.status === 'opened' && (
                          <span className="text-gray-400">
                            Opened {formatDate(item.opened_at)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-3">
                      <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {status.label}
                      </span>

                      {item.status === 'failed' && (
                        <button
                          onClick={() => onResend(item.id)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Resend"
                        >
                          <RefreshCw className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Error Message */}
                  {item.error_message && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-600">{item.error_message}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityTab;
